import { useCallback, useState, useEffect } from 'react'
import _ from 'lodash'

const useVideo = (videoRef, type, startTime, endTime, startMuted = true) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [volume, setVolume] = useState(1)
    const [isMuted, setIsMuted] = useState(startMuted)
    const [isReady, setIsReady] = useState(false)
    const [isSeekComplete, setIsSeekComplete] = useState(false)

    // Handle trim values - use video duration as fallback when not provided
    const [videoDuration, setVideoDuration] = useState(0)

    // Set safe trim values: use provided values or fallback to full video (0 to duration)
    const safeStartTime = _.isNumber(startTime) && startTime >= 0 ? startTime : 0
    const safeEndTime = _.isNumber(endTime) && endTime > 0 ? endTime : videoDuration || 0
    const calculatedDuration = safeEndTime - safeStartTime

    // Add effect for segment control when startTime and endTime are provided
    useEffect(() => {
        const video = videoRef.current
        if (!video || type !== 'video') return

        let hasSeekToStart = false

        const handleLoadedData = () => {
            if (!hasSeekToStart && safeStartTime > 0) {
                const seekToStart = () => {
                    if (video.readyState >= 2) {
                        video.currentTime = safeStartTime
                        hasSeekToStart = true
                        setIsReady(true)
                        // Wait for seek to complete before marking as ready for drawing
                        const checkSeekComplete = () => {
                            if (Math.abs(video.currentTime - safeStartTime) < 0.1) setIsSeekComplete(true)
                            else setTimeout(checkSeekComplete, 50)
                        }
                        setTimeout(checkSeekComplete, 50)
                    } else {
                        setTimeout(seekToStart, 50)
                    }
                }
                seekToStart()
            } else {
                setIsReady(true)
                setIsSeekComplete(true)
            }
        }

        const handleCanPlay = () => {
            if (!hasSeekToStart && safeStartTime > 0 && video.readyState >= 3) {
                video.currentTime = safeStartTime
                hasSeekToStart = true
                // Wait for seek to complete
                const checkSeekComplete = () => {
                    if (Math.abs(video.currentTime - safeStartTime) < 0.1) {
                        setIsSeekComplete(true)
                    } else {
                        setTimeout(checkSeekComplete, 50)
                    }
                }
                setTimeout(checkSeekComplete, 50)
            } else {
                setIsSeekComplete(true)
            }
            setIsReady(true)
        }

        const handleSegmentTimeUpdate = () => {
            const current = video.currentTime

            // If we have segment boundaries, enforce them strictly
            if (current < safeStartTime) {
                video.currentTime = safeStartTime
            } else if (current >= safeEndTime) {
                video.currentTime = safeStartTime // Loop back to start
            }
            // Set relative currentTime for progress calculation
            setCurrentTime(Math.max(0, current - safeStartTime))
        }

        // Only add segment-specific listeners if we have segment boundaries
        // if (isTrimmed) {
        setIsReady(false)
        hasSeekToStart = false

        video.addEventListener('loadeddata', handleLoadedData)
        video.addEventListener('canplay', handleCanPlay)
        video.addEventListener('timeupdate', handleSegmentTimeUpdate)

        return () => {
            video.removeEventListener('loadeddata', handleLoadedData)
            video.removeEventListener('canplay', handleCanPlay)
            video.removeEventListener('timeupdate', handleSegmentTimeUpdate)
        }
    }, [safeStartTime, safeEndTime, type])

    const handlePlay = useCallback(() => setIsPlaying(true), [])
    const handlePause = useCallback(() => setIsPlaying(false), [])

    const handlePlayPause = useCallback(() => {
        const video = videoRef.current
        if (!video || type !== 'video') return
        video.paused ? video.play() : video.pause()
    }, [type, videoRef])

    const handleDurationChange = useCallback(() => {
        const video = videoRef.current

        if (video && type === 'video' && video.duration) {
            // Store the actual video duration
            setVideoDuration(video.duration)
        }
    }, [type, videoRef])

    const handleSeek = useCallback(
        (e) => {
            const video = videoRef.current
            if (!video || type !== 'video') return

            const rect = e.currentTarget.getBoundingClientRect()
            const x = e.clientX - rect.left
            const percentage = x / rect.width

            // For segments, seek within the segment range
            const segmentDuration = safeEndTime - safeStartTime
            video.currentTime = safeStartTime + percentage * segmentDuration
        },
        [type, safeStartTime, safeEndTime, videoRef],
    )

    const handleVolumeChange = useCallback(
        (e) => {
            const video = videoRef.current
            if (!video || type !== 'video') return

            const newVolume = parseFloat(e.target.value)
            setVolume(newVolume)
            video.volume = newVolume
            setIsMuted(newVolume === 0)
        },
        [type, videoRef],
    )

    const handleMuteToggle = useCallback(() => {
        const video = videoRef.current
        if (!video || type !== 'video') return

        if (isMuted) {
            video.muted = false
            setIsMuted(false)

            if (volume === 0) {
                setVolume(1)
                video.volume = 1
            }
        } else {
            video.muted = true
            setIsMuted(true)
        }
    }, [type, isMuted, volume, videoRef])

    return {
        isPlaying,
        currentTime,
        duration: calculatedDuration,
        volume,
        isMuted,
        isReady,
        isSeekComplete,
        setIsMuted,
        setVolume,
        handlePlay,
        handlePause,
        handlePlayPause,
        handleDurationChange,
        handleSeek,
        handleVolumeChange,
        handleMuteToggle,
    }
}

export default useVideo
